
import React from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

interface CompactNewChatButtonProps {
  onClick?: () => void;
  className?: string;
  loading?: boolean;
}

export const CompactNewChatButton = ({ onClick, className, loading = false }: CompactNewChatButtonProps) => {
  const handleNewChat = () => {
    onClick?.(); // Call external onClick handler (parent handles MCP chat reset)
  };

  return (
    <motion.div
      className={cn("relative", className)}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Button
        onClick={handleNewChat}
        size="sm"
        variant="ghost"
        className={cn(
          "relative h-7 px-3 rounded-lg",
          "bg-white/5 hover:bg-white/10 backdrop-blur-sm",
          "border border-white/10 hover:border-white/20",
          "text-foreground/70 hover:text-foreground font-medium text-xs",
          "shadow-sm hover:shadow-md",
          "transition-all duration-200 ease-out",
          "group overflow-hidden",
          loading && "opacity-50 cursor-not-allowed"
        )}
        aria-label="New conversation"
        disabled={loading}
      >
        {/* Subtle hover effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/8 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg" />
        
        {/* Content */}
        <motion.div
          className="relative flex items-center gap-1.5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          {loading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <motion.div
              animate={{ 
                rotate: [0, 90, 0],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Plus className="h-3 w-3" />
            </motion.div>
          )}
          
          <span className="text-xs font-medium">New</span>
        </motion.div>
      </Button>
    </motion.div>
  );
};
